import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { z } from 'zod';

import {
  apiErrorSchema,
  refreshResponseSchema,
  type ApiErrorCode,
} from '@midiautolistening/contracts';

import {
  accessTokenNeedsRefresh,
  applyTokens,
  clearSession,
  notifySessionExpired,
  session,
} from './session';

/**
 * The HTTP layer.
 *
 * Two rules hold everywhere in this file:
 *
 *  1. **Every response is parsed with its contract schema.** A server that drifts
 *     from `@midiautolistening/contracts` fails loudly here with
 *     `CONTRACT_MISMATCH` instead of rendering `undefined` three components deep.
 *  2. **A 401 buys exactly one refresh.** The retry flag lives on the request
 *     config, and concurrent 401s share a single in-flight refresh, so a burst of
 *     parallel requests cannot rotate the refresh token several times over — which
 *     the server treats as token theft and answers by revoking the whole family.
 */

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

/** Paths that must never trigger the refresh-and-replay dance. */
const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh'];

export type ApiFailureCode =
  | ApiErrorCode
  | 'NETWORK_ERROR'
  | 'CONTRACT_MISMATCH'
  | 'UNKNOWN_ERROR';

export class ApiRequestError extends Error {
  readonly code: ApiFailureCode;
  readonly status: number | null;
  readonly details: Record<string, string[]> | null;
  readonly requestId: string | null;

  constructor(
    message: string,
    code: ApiFailureCode,
    status: number | null = null,
    details: Record<string, string[]> | null = null,
    requestId: string | null = null,
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.requestId = requestId;
  }

  /**
   * True when the server answered "no such route" rather than "no such record".
   * Several screens in this MVP are built against a contract whose endpoint is
   * not implemented yet; the error state says so instead of showing an empty list.
   */
  get looksLikeMissingEndpoint(): boolean {
    return (this.status === 404 || this.status === 501) && this.code !== 'NOT_FOUND';
  }
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  /** Set once a request has already been replayed after a token refresh. */
  __refreshRetry?: boolean;
}

/**
 * Query strings use repeated keys (`status=RUNNING&status=PAUSED`) because that is
 * what the Zod array filters on the server accept. Axios' default `key[]=` form
 * would arrive as an unknown parameter and be dropped.
 */
function serializeParams(params: Record<string, unknown>): string {
  const search = new URLSearchParams();

  const append = (key: string, value: unknown): void => {
    if (value === undefined || value === null || value === '') return;
    search.append(key, String(value));
  };

  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const item of value) append(key, item);
    } else {
      append(key, value);
    }
  }

  return search.toString();
}

function createClient(withAuth: boolean): AxiosInstance {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json' },
    paramsSerializer: { serialize: serializeParams },
  });

  if (withAuth) {
    instance.interceptors.request.use((config) => {
      // Always overwrite rather than fill in: after a refresh the replayed request
      // still carries the old header, and a stale token would 401 forever.
      if (session.accessToken) {
        config.headers.set('Authorization', `Bearer ${session.accessToken}`);
      } else {
        config.headers.delete('Authorization');
      }
      return config;
    });
  }

  return instance;
}

/** Used only for `/auth/refresh`, so a failing refresh cannot recurse into itself. */
const rawClient = createClient(false);
const client = createClient(true);

let refreshInFlight: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refreshToken = session.refreshToken;
  if (!refreshToken) {
    throw new ApiRequestError('Phiên đăng nhập đã kết thúc', 'UNAUTHENTICATED', 401);
  }

  const response = await rawClient.post('/auth/refresh', { refreshToken });
  const parsed = refreshResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new ApiRequestError(
      'Phản hồi /auth/refresh không khớp hợp đồng',
      'CONTRACT_MISMATCH',
      response.status,
      zodIssuesToDetails(parsed.error),
    );
  }

  applyTokens(parsed.data);
  return parsed.data.accessToken;
}

/** Single-flight: parallel 401s wait on one refresh instead of racing. */
function refreshAccessToken(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Refresh proactively when the access token is about to lapse.
 *
 * The WebSocket needs this: it puts the token in the handshake URL, and a socket
 * opened with a token that expires seconds later would be dropped and reconnect in
 * a loop. Returns null when there is no session at all.
 */
export async function ensureAccessToken(): Promise<string | null> {
  if (!session.refreshToken) return session.accessToken;
  if (!accessTokenNeedsRefresh()) return session.accessToken;

  try {
    return await refreshAccessToken();
  } catch {
    clearSession();
    notifySessionExpired();
    return null;
  }
}

function isAuthPath(url: string | undefined): boolean {
  if (!url) return false;
  return AUTH_PATHS.some((path) => url.startsWith(path));
}

client.interceptors.response.use(undefined, async (error: unknown) => {
  if (!(error instanceof AxiosError)) throw toApiError(error);

  const config = error.config as RetriableConfig | undefined;
  const shouldRefresh =
    error.response?.status === 401 &&
    config !== undefined &&
    config.__refreshRetry !== true &&
    !isAuthPath(config.url) &&
    session.refreshToken !== null;

  if (!shouldRefresh || !config) throw toApiError(error);

  config.__refreshRetry = true;

  try {
    await refreshAccessToken();
  } catch {
    // The refresh token itself is dead. Drop the session and let the app route
    // to /login rather than leaving half-authenticated state around.
    clearSession();
    notifySessionExpired();
    throw toApiError(error);
  }

  // The request interceptor re-attaches the freshly rotated access token.
  return client.request(config);
});

function zodIssuesToDetails(error: z.ZodError): Record<string, string[]> {
  const details: Record<string, string[]> = {};
  for (const issue of error.issues) {
    const key = issue.path.length > 0 ? issue.path.join('.') : '(root)';
    const bucket = details[key];
    if (bucket) bucket.push(issue.message);
    else details[key] = [issue.message];
  }
  return details;
}

/** Fallback mapping for error bodies that are not in our own envelope shape. */
function codeForStatus(status: number): ApiFailureCode {
  if (status === 400 || status === 422) return 'VALIDATION_ERROR';
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 500) return 'INTERNAL_ERROR';
  return 'UNKNOWN_ERROR';
}

export function toApiError(error: unknown): ApiRequestError {
  if (error instanceof ApiRequestError) return error;

  if (error instanceof AxiosError) {
    if (!error.response) {
      return new ApiRequestError(
        'Không kết nối được tới máy chủ. Kiểm tra API có đang chạy không.',
        'NETWORK_ERROR',
      );
    }

    const status = error.response.status;
    const parsed = apiErrorSchema.safeParse(error.response.data);
    if (parsed.success) {
      return new ApiRequestError(
        parsed.data.message,
        parsed.data.code,
        parsed.data.statusCode,
        parsed.data.details ?? null,
        parsed.data.requestId ?? null,
      );
    }

    const body = error.response.data as { message?: unknown } | undefined;
    const message =
      typeof body?.message === 'string' ? body.message : `Yêu cầu thất bại (HTTP ${status})`;
    return new ApiRequestError(message, codeForStatus(status), status);
  }

  if (error instanceof Error) {
    return new ApiRequestError(error.message, 'UNKNOWN_ERROR');
  }

  return new ApiRequestError('Lỗi không xác định', 'UNKNOWN_ERROR');
}

/**
 * Issue a request and validate the response against its contract schema.
 *
 * The schema argument is not optional anywhere in this app: an unvalidated
 * response is exactly the failure mode this layer exists to prevent.
 *
 * Generic over the schema rather than over its output type: several contract
 * schemas carry `.default()` values, which makes their Zod input and output types
 * differ, and a `z.ZodType<T>` parameter would silently reject exactly those.
 */
export async function request<Schema extends z.ZodTypeAny>(
  schema: Schema,
  config: AxiosRequestConfig,
): Promise<z.output<Schema>> {
  let status: number | null = null;
  let data: unknown;

  try {
    const response = await client.request(config);
    status = response.status;
    data = response.data;
  } catch (error) {
    throw toApiError(error);
  }

  const parsed = schema.safeParse(data);
  if (!parsed.success) {
    throw new ApiRequestError(
      `Máy chủ trả về dữ liệu không khớp hợp đồng tại ${config.method?.toUpperCase() ?? 'GET'} ${config.url ?? ''}`,
      'CONTRACT_MISMATCH',
      status,
      zodIssuesToDetails(parsed.error),
    );
  }

  return parsed.data;
}

export function apiGet<Schema extends z.ZodTypeAny>(
  url: string,
  schema: Schema,
  params?: Record<string, unknown>,
): Promise<z.output<Schema>> {
  return request(schema, { method: 'GET', url, params });
}

export function apiPost<Schema extends z.ZodTypeAny>(
  url: string,
  schema: Schema,
  body?: unknown,
): Promise<z.output<Schema>> {
  return request(schema, { method: 'POST', url, data: body ?? {} });
}

export function apiPatch<Schema extends z.ZodTypeAny>(
  url: string,
  schema: Schema,
  body?: unknown,
): Promise<z.output<Schema>> {
  return request(schema, { method: 'PATCH', url, data: body ?? {} });
}

export function apiDelete<Schema extends z.ZodTypeAny>(
  url: string,
  schema: Schema,
): Promise<z.output<Schema>> {
  return request(schema, { method: 'DELETE', url });
}

/**
 * Fetch a binary through the authenticated client and hand it back as a Blob.
 *
 * Used by the agent-installer download button. The filename is the server's to
 * decide, read from `content-disposition` — RFC 5987 UTF-8 form first, plain
 * quoted form as the fallback.
 */
export async function apiDownload(url: string): Promise<{ blob: Blob; fileName: string | null }> {
  try {
    const response = await client.request({
      method: 'GET',
      url,
      responseType: 'blob',
      /**
       * No deadline on this one request.
       *
       * The client's 30s default is sized for JSON calls. The agent installer is
       * ~230 MB, which needs roughly 60 Mbps sustained just to arrive inside that
       * window — over a normal link it does not, axios aborts mid-stream, and
       * because the abort leaves no response behind the failure surfaces as
       * NETWORK_ERROR: "không kết nối được tới máy chủ". The server answered
       * perfectly and is still streaming when the client hangs up.
       */
      timeout: 0,
    });
    const disposition = String(
      (response.headers as Record<string, unknown>)['content-disposition'] ?? '',
    );
    const utf8Name = /filename\*=UTF-8''([^;]+)/iu.exec(disposition);
    const plainName = /filename="([^"]+)"/iu.exec(disposition);
    const fileName = utf8Name?.[1] ? decodeURIComponent(utf8Name[1]) : (plainName?.[1] ?? null);
    return { blob: response.data as Blob, fileName };
  } catch (error) {
    throw toApiError(error);
  }
}
